import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  CMSSubscriptionInterface,
  DoceboSubscriptionInterface,
  HasAnalytics,
} from '@app/interfaces/_index';
import { CardType, CardTypeCallToActions, ProductShortName } from '@app/enums/_index';
import {
  GoogleAnalyticsService,
  SubscriptionService,
  WordpressSubscriptionsService,
} from '@app/services/_index';
import { LMSSubscription } from '@app/models/_index';
import { NguCarouselConfig } from '@ngu/carousel';
import { Subscription } from 'rxjs';
import { SubscriptionAdapter } from '@app/adapters/_index';
import { SubscriptionFactory } from '@app/factories/_index';
import { environment } from '@environments/environment';
import { logGoogleAnalytics } from '@app/utilities/_index';

@Component({
  selector: 'app-subscription-list',
  templateUrl: './subscription-list.component.html',
  styleUrls: ['./subscription-list.component.scss'],
  standalone: false,
})
export class SubscriptionListComponent
  implements OnInit, OnDestroy, AfterViewChecked, HasAnalytics
{
  carouselTile: NguCarouselConfig;

  @Input() isLightMode = true; // Toggle to change background styling from light or dark

  // Toggle docebo subscription content vs dynamic content
  loadDoceboSubscriptionContent = false;
  subscriptionItems: LMSSubscription[] = [];
  isLoading: boolean;
  // NOTE: subscription id = 190 is Genesys Engage Learning Subscription
  // NOTE: subscription id = 189 is PureConnect Learning Subscription
  // NOTE: subscription id = 84 is Genesys Cloud Learning Subscription
  // NOTE: subscription id = 497 (Docebo Sandbox and Docebo Production) is Pointillist Learning Subscription
  subscriptionIds: number[] = environment.beyond.subscription_ids;

  sub: Subscription;
  @Input() carouselDotActiveCSS = 'default-active'; // Set background color for carousel dots

  constructor(
    private cdRef: ChangeDetectorRef,
    private googleAnalyticsService: GoogleAnalyticsService,
    private subscriptionService: SubscriptionService,
    private wpSubscriptionService: WordpressSubscriptionsService,
    private subscriptionFactory: SubscriptionFactory,
    private subsAdapter: SubscriptionAdapter
  ) {
    this.carouselTile = {
      grid: { xs: 1, sm: 2, md: 3, lg: 4, all: 0 }, // XS shows 1 slide, SM shows 2 slides, M and greater show 3
      slide: 3,
      speed: 400,
      animation: 'lazy',
      point: { visible: false },
      load: 3,
      touch: true,
      easing: 'ease',
    };
  }

  /**
   * OnInit load subscriptions
   */
  ngOnInit(): void {
    // Check if subscriptions are from WordPress CMS
    this.getSubscriptions();
  }

  /**
   * After view Checks if any changes made to the carousel with the
   * angular change detection reference
   */
  ngAfterViewChecked(): void {
    this.cdRef.detectChanges();
  }

  /**
   * On Destroy, unsubscribe subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.loadDoceboSubscriptionContent && this.sub) {
      this.sub.unsubscribe();
    }
  }

  /**
   * Set and load CMS or Docebo Subscriptions
   */
  getSubscriptions(): void {
    // checking loading from decobo or WordPress CMS
    if (!this.loadDoceboSubscriptionContent) {
      // Default word press CMS
      this.isLoading = true;
      this.sub = this.wpSubscriptionService.getSubscriptionList().subscribe({
        next: (subscriptionItems: CMSSubscriptionInterface[]) => {
          // Sort the subscription cards based on display order acf field.
          subscriptionItems.sort(
            (a, b) =>
              a['acf'].display_order &&
              a['acf'].display_order
                .toLocaleString()
                .localeCompare(b['acf'].display_order.toLocaleString())
          );
          this.isLoading = false;
          // TODO: Use rxjs map
          subscriptionItems.forEach((item: CMSSubscriptionInterface) => {
            const subscriptionItem: LMSSubscription = this.subsAdapter.adapt(item);
            if (subscriptionItem) {
              this.subscriptionItems.push(subscriptionItem);
            }
          });
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Subscriptions List content completed');
        },
      });
    } else {
      // NOTE: Toggle dropdown needs to match slug name
      // Else pull subscription information from Docebo directly if CMS is unavailable
      this.sub = this.subscriptionService.getSubscriptions(this.subscriptionIds).subscribe({
        next: (items: DoceboSubscriptionInterface[]) => {
          this.isLoading = false;
          items
            .filter((nonulls) => nonulls)
            .forEach((item: DoceboSubscriptionInterface) => {
              const subscriptionItem = this.subsAdapter.adaptFromLMS(item);
              if (subscriptionItem) {
                this.subscriptionItems.push(subscriptionItem);
              }
            });
          // TODO: Add backup fallback for Agent Platform (AP) similar to BeyondCX
          this.subscriptionItems.push(
            this.subscriptionFactory.getSubscription(ProductShortName.BEYOND_CX)
          );
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Subscriptions List content completed');
        },
      });
    }
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * Add Track By Subscription Item Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of subscriptionItem array
   * @param item - Subscription item
   * @returns enrollment id
   */
  trackBySubscriptionItemId(index: number, subscriptionItem: LMSSubscription): number {
    if (subscriptionItem) {
      return subscriptionItem.id;
    } else {
      return index;
    }
  }

  /**
   *  CardTypeCallToActions enums based on card types
   */
  public get cardTypeCallToActions(): typeof CardTypeCallToActions {
    return CardTypeCallToActions;
  }

  /**
   *  CardType enums based on card types
   */
  public get cardType(): typeof CardType {
    return CardType;
  }
}
